import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { switchUserExit, logout } from 'redux/actions/user';
import { URLS } from 'common/urls';
import { useHistory } from 'react-router-dom';

const SUBanner = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector((state) => state.user);

    const isSU = user?.is_su;
    const suAuditLogId = user?.su_audit_log_id;
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isExpired, setIsExpired] = useState(false);

    // Handle exit/logout
    const handleExit = useCallback(
        async (autoLogout = false) => {
            try {
                // Call exit API if we have audit log ID
                if (suAuditLogId) {
                    await dispatch(switchUserExit(suAuditLogId));
                }

                // Logout and clear everything
                await dispatch(logout());

                // Redirect to login
                history.push(URLS.LOGIN);
            } catch (err) {
                console.error('Exit switch user error:', err);
                // Force logout anyway
                await dispatch(logout());
                history.push(URLS.LOGIN);
            }
        },
        [dispatch, history, suAuditLogId]
    );

    // Calculate time remaining
    useEffect(() => {
        if (!isSU || !user.su_session_expires_at) {
            return;
        }

        const updateTimer = () => {
            const expiresAt = new Date(user.su_session_expires_at);
            const now = new Date();
            const diff = expiresAt - now;

            if (diff <= 0) {
                setIsExpired(true);
                setTimeRemaining('Session expired');
            } else {
                const minutes = Math.floor(diff / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);
                setTimeRemaining(`${minutes}m ${seconds}s`);
            }
        };

        // Update immediately
        updateTimer();

        // Update every second
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [isSU, user.su_session_expires_at]);

    // Auto-logout when session expires
    useEffect(() => {
        if (isExpired) {
            handleExit(true);
        }
    }, [isExpired, handleExit]);

    // Add class to body when banner is visible
    useEffect(() => {
        if (isSU) {
            document.body.classList.add('su-active');
            return () => {
                document.body.classList.remove('su-active');
            };
        }
    }, [isSU]);

    // Only show banner if user is in switch user mode
    if (!user || !isSU) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10000,
                backgroundColor: isExpired ? '#f44336' : '#ff9800',
                color: '#fff',
                padding: '10px 20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontFamily: 'Arial, sans-serif',
                height: '50px'
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span
                    role="img"
                    aria-label="warning"
                    style={{
                        fontSize: '20px',
                        fontWeight: 'bold'
                    }}>
                    {isExpired ? '🔒' : '⚠️'}
                </span>
                <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                        You are logged in as{' '}
                        <span style={{ textDecoration: 'underline' }}>
                            {user.email || user.username}
                        </span>
                        {timeRemaining && (
                            <span
                                style={{
                                    marginLeft: '10px',
                                    fontSize: '12px'
                                }}>
                                ({timeRemaining})
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: '11px', opacity: 0.9 }}>
                        Admin: {user.su_admin_email}
                        {isExpired && ' - Session expired, logging out...'}
                    </div>
                </div>
            </div>
            <button
                onClick={() => handleExit(false)}
                disabled={isExpired}
                style={{
                    padding: '6px 16px',
                    backgroundColor: '#fff',
                    color: isExpired ? '#999' : '#ff9800',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    cursor: isExpired ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                    opacity: isExpired ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                    if (!isExpired) {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 3px 6px rgba(0,0,0,0.2)';
                    }
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}>
                Exit Switch User
            </button>
        </div>
    );
};

export default SUBanner;
